import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getCurrentOrigin } from "@/config/appConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Users, AlertCircle, UserX, Eye, EyeOff, Mail, Lock, User, Sparkles, ArrowRight, Shield } from "lucide-react";
import { getSessionItem, removeLocalItem, removeSessionItem, setSessionItem } from "@/utils/safeStorage";
import { parseAcceptInviteResponse, isRetryableInviteError } from "@/utils/inviteErrors";

const ONBOARDING_PATH = "/onboarding";

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;


export function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, signInWithGoogle, signInWithAzure } = useAuth();
  const queryClient = useQueryClient();

  // Add diagnostic logging
  useEffect(() => {
    console.log("[JoinPage] Mounted with:", {
      token: token ? `${token.substring(0, 8)}...` : "NO TOKEN",
      hasUser: !!user,
      userEmail: user?.email || "none",
      pathname: window.location.pathname,
      href: window.location.href,
    });
  }, [token, user]);

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [coachName, setCoachName] = useState<string | null>(null);
  
  // Two-step flow: show CTA, then show form
  const [hasAcceptedInvite, setHasAcceptedInvite] = useState(false);
  const [acceptState, setAcceptState] = useState<"idle" | "accepting" | "accepted" | "already-linked" | "error">("idle");
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [shouldAcceptInvite, setShouldAcceptInvite] = useState(false);
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const invitePath = token ? `/join/${token}` : "/auth";
  const isDev = import.meta.env.DEV;

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        if (isDev) {
          const origin = typeof window !== "undefined" ? window.location.origin : "unknown";
          console.log("[JoinPage] No invite token present", { origin });
        }
        setIsValidating(false);
        return;
      }

      try {
        if (isDev) {
          const origin = typeof window !== "undefined" ? window.location.origin : "unknown";
          console.log("[JoinPage] Validating invite token", {
            token,
            length: token.length,
            origin,
          });
        }
        // Use RPC to validate token (public, no auth required)
        const { data, error } = await supabase.rpc("validate_invite_token", {
          invite_token: token,
        });
        
        const result = data as { valid: boolean; coach_name?: string } | null;

        if (isDev) {
          console.log("[JoinPage] validate_invite_token result", {
            valid: Boolean(result?.valid),
            error: error?.message ?? null,
          });
        }

        if (error || !result?.valid) {
          setIsValid(false);
          setIsValidating(false);
          return;
        }

        setCoachName(result.coach_name || "Your Coach");
        setIsValid(true);
        setIsValidating(false);

        // Store token for after signup (sessionStorage only — localStorage cleared on sign-out
        // but could persist across accounts if sign-out is missed)
        const pendingInvitePayload = JSON.stringify({ token });
        setSessionItem("pendingInvite", pendingInvitePayload);
        setSessionItem("pendingInviteToken", token);
      } catch (err) {
        console.error("Error validating invite:", err);
        setIsValid(false);
        setIsValidating(false);
      }
    };

    validateToken();
  }, [isDev, token]);

  const [isCoachUser, setIsCoachUser] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const refreshCoachSwitchState = useCallback(async () => {
    await supabase.auth.getSession();
    if (user?.id) {
      await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
    }

    const queryKeyMatchers = [
      "profile",
      "user-settings",
      "coach",
      "leader",
      "behavior",
      "meeting",
      "moment",
      "insight",
      "transcript",
      "nudge",
      "schedule",
      "notification",
      "solo-mode-experience-state",
      "invitation-context",
      "solo-leader-billing-state",
      "meeting-capture-entitlement",
      "billing-ownership",
      "trial-access",
    ];

    const matchesCoachSwitch = (queryKey: readonly unknown[]) =>
      queryKey.some((key) =>
        queryKeyMatchers.some((matcher) =>
          String(key).toLowerCase().includes(matcher)
        )
      );

    await queryClient.invalidateQueries({
      predicate: (query) => matchesCoachSwitch(query.queryKey),
    });
    await queryClient.refetchQueries({
      predicate: (query) => matchesCoachSwitch(query.queryKey),
    });

    try {
      localStorage.removeItem("veera-chat-session-id");
      localStorage.removeItem("viewed-transcript-analyses");
    } catch (error) {
      console.warn("Unable to clear cached coach context:", error);
    }
  }, [queryClient, user?.id]);

  // Handle logged-in users
  useEffect(() => {
    const processInviteForLoggedInUser = async () => {
      if (!user || !isValid || !token || acceptState !== "idle" || !shouldAcceptInvite) return;

      console.log("[JoinPage] Attempting to accept invite for logged-in user:", {
        email: user.email,
        userId: user.id,
      });

      setAcceptState("accepting");
      setAcceptError(null);

      try {
        const { data, error } = await supabase.functions.invoke("accept-invite", {
          body: { token },
        });

        console.log("[JoinPage] accept-invite response:", {
          hasError: !!error,
          dataOk: data?.ok,
          dataError: data?.error,
          errorMessage: error?.message,
        });

        const result = parseAcceptInviteResponse(data, error);

        if (!result.ok) {
          if (error) {
            console.error("Error accepting invite:", error);
          }

          // Coach/self-invite errors
          if (result.error === "coach_account" || result.error === "self_invite") {
            setIsCoachUser(true);
            setAcceptState("error");
            return;
          }

          // Auto-retry up to 3 times for auth/timing errors
          if (isRetryableInviteError(result, error) && retryCount < 3) {
            const delay = Math.pow(2, retryCount) * 1000;
            if (isDev) {
              console.log(`[JoinPage] Auth error, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
            }
            setRetryCount(retryCount + 1);
            setTimeout(() => {
              setAcceptState("idle");
            }, delay);
            return;
          }

          setAcceptState("error");
          setAcceptError(result.message || "We couldn't accept this invite. Please request a new link.");
          return;
        }

        // Success - reset retry count and clear pending invite
        setRetryCount(0);
        removeSessionItem("pendingInvite");
        removeSessionItem("pendingInviteToken");
        removeLocalItem("pendingInvite");
        removeLocalItem("pendingInviteToken");

        await refreshCoachSwitchState();
        const destination = data.onboardingComplete ? "/today" : ONBOARDING_PATH;
        setAcceptState(data.alreadyLinked ? "already-linked" : "accepted");
        navigate(destination, { replace: true });
      } catch (err) {
        console.error("Error accepting invite:", err);

        // Auto-retry unexpected errors (might be network/timing issues)
        if (retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          if (isDev) {
            console.log(`[JoinPage] Unexpected error, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`, err);
          }
          setRetryCount(retryCount + 1);
          setTimeout(() => {
            setAcceptState("idle"); // Reset to trigger another attempt
          }, delay);
          return;
        }

        setAcceptState("error");
        setAcceptError("We couldn't accept this invite. Please try again or request a new link.");
      }
    };

    processInviteForLoggedInUser();
  }, [user, isValid, token, acceptState, shouldAcceptInvite, navigate, isDev, retryCount, refreshCoachSwitchState]);

  const handleUseDifferentAccount = async () => {
    try {
      await supabase.auth.signOut();
      // Force reload to clear all in-memory session state
      window.location.href = invitePath;
    } catch (error) {
      console.error("Failed to sign out:", error);
      // Still reload even if signOut fails to clear cached state
      window.location.href = invitePath;
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");

    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);

    try {
      setSessionItem("signupRole", "leader");
      setSessionItem("justSignedUp", "true");

      const redirectUrl = `${getCurrentOrigin()}${invitePath}`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            role: "leader",
          },
        },
      });
      
      if (error) {
        removeSessionItem("signupRole");
        throw error;
      }

      // If email confirmation is required, session may be null here.
      // Avoid leaving the CTA button in a perpetual loading state.
      if (!data.session) {
        toast.success("Account created. Check your email to confirm, then sign in.");
        setShowSignIn(true);
        setIsSubmitting(false);
        return;
      }

      setShouldAcceptInvite(true);
      navigate(invitePath);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to sign up"));
      setIsSubmitting(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      setSessionItem("signupRole", "leader");
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        removeSessionItem("signupRole");
        throw error;
      }

      setShouldAcceptInvite(true);
      navigate(invitePath);
    } catch (error: unknown) {
      const message = getErrorMessage(error, "Failed to sign in");
      toast.error(
        message === "Invalid login credentials"
          ? "We couldn't find an account with that email and password. Use the same email your coach invited, or create a leader account first."
          : message
      );
      setIsSubmitting(false);
    }
  };

  // Auto-accept for users who just signed up (flag-based, not timestamp-based).
  // Keep this hook above conditional returns so hook order is stable.
  useEffect(() => {
    if (user && isValid && acceptState === "idle" && !shouldAcceptInvite) {
      const justSignedUp = getSessionItem("justSignedUp") === "true";
      if (justSignedUp) {
        removeSessionItem("justSignedUp");
        setShouldAcceptInvite(true);
      }
    }
  }, [user, isValid, acceptState, shouldAcceptInvite]);

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        
        {isDev && (
          <div className="fixed bottom-4 left-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs font-mono dark:bg-yellow-950/30 dark:border-yellow-800 max-w-xs">
            <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">QA Debug Panel</p>
            <p className="text-yellow-700 dark:text-yellow-300">Token: {token ? "present" : "missing"}</p>
            <p className="text-yellow-700 dark:text-yellow-300">Validating: {isValidating ? "yes" : "no"}</p>
            <p className="text-yellow-700 dark:text-yellow-300">Accepting: {acceptState === "accepting" ? "yes" : "no"}</p>
          </div>
        )}
      </div>
    );
  }

  // Accepting invite state
  if (acceptState === "accepting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">
            {retryCount > 0
              ? `Connecting you to your coach... (attempt ${retryCount + 1})`
              : "Connecting you to your coach..."}
          </p>
        </div>
      </div>
    );
  }

  if (user && isValid && acceptState === "idle" && !shouldAcceptInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>You're already signed in</CardTitle>
            <CardDescription className="text-base">
              Continue as <span className="font-medium text-foreground">{user.email}</span> or use a different account to join as a new leader.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full" onClick={() => setShouldAcceptInvite(true)}>
              Continue with this account
            </Button>
            <Button variant="outline" className="w-full" onClick={handleUseDifferentAccount}>
              Use a different account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Coach trying to use a leader invite
  if (isCoachUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
              <UserX className="h-6 w-6 text-amber-500" />
            </div>
            {isDev && (
              <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs font-mono dark:bg-yellow-950/30 dark:border-yellow-800 text-left">
                <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Debug Info</p>
                <p className="text-yellow-700 dark:text-yellow-300">Current user: {user?.email}</p>
                <p className="text-yellow-700 dark:text-yellow-300">Trying to accept invite for coach: {coachName}</p>
              </div>
            )}
            <CardTitle>This Is Your Own Invite Link</CardTitle>
            <CardDescription>
              You cannot accept your own invite link. This link is meant for leaders you want to coach. Share it with them instead!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button asChild className="w-full">
              <Link to="/coach">Go to Coach home</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/coach/leaders">Invite Your Own Leaders</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invalid Invite Link</CardTitle>
            <CardDescription className="text-base">
              This invite link is not valid. Please ask your coach for a new invite link.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild variant="outline" className="w-full">
              <Link to="/auth">Go to Sign In</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (acceptState === "error" && acceptError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            {isDev && user && (
              <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs font-mono dark:bg-yellow-950/30 dark:border-yellow-800 text-left">
                <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Debug Info</p>
                <p className="text-yellow-700 dark:text-yellow-300">Current user: {user.email}</p>
                <p className="text-yellow-700 dark:text-yellow-300">Coach name: {coachName || "Unknown"}</p>
                <p className="text-yellow-700 dark:text-yellow-300">Check browser console for more details</p>
              </div>
            )}
            <CardTitle>We couldn&apos;t accept this invite</CardTitle>
            <CardDescription className="text-base">
              {acceptError}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild className="w-full">
              <Link to="/auth">Sign in or try again</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/auth">Request a new invite link</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 1: Valid invite - show context and CTA
  if (!hasAcceptedInvite && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-2xl">
                You've been invited to review leadership moments
              </CardTitle>
              <CardDescription className="text-base">
                <span className="font-medium text-foreground">{coachName}</span> has invited you to use Veera to review leadership moments together.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 rounded-xl bg-muted/50 border border-border space-y-3">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Veera supports coaching by organizing moments between sessions for review.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  You&apos;ll confirm your name and email before accepting this invite.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                onClick={() => {
                  setHasAcceptedInvite(true);
                  setShowSignIn(true);
                }}
                className="w-full h-12 text-base"
              >
                Connect with your coach
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  setHasAcceptedInvite(true);
                  setShowSignIn(false);
                }}
                className="w-full h-12 text-base"
              >
                Create a new leader account
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Use the same email address your coach invited. If you already use Veera, this adds leader access to your existing account.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Signup/Signin form after accepting
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>
            {showSignIn ? "Connect with your coach" : "Create a new leader account"}
          </CardTitle>
          <CardDescription>
            {showSignIn 
              ? "Sign in with the same email your coach invited. This connects your existing Veera account to the coach relationship."
              : `New to Veera? Create an account with the email ${coachName} invited.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={showSignIn ? handleSignIn : handleSignUp} className="space-y-4">
            {!showSignIn && (
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="fullName"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={showSignIn ? "Enter your password" : "Create a password (min 6 characters)"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {!showSignIn && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError("");
                    }}
                    className="pl-10 h-11"
                    required
                    minLength={6}
                  />
                </div>
                {passwordError && (
                  <p className="text-sm text-destructive">{passwordError}</p>
                )}
              </div>
            )}

            <Button type="submit" className="w-full h-11" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : showSignIn ? (
                "Sign in and connect"
              ) : (
                "Create account and connect"
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-11"
              disabled={isSubmitting}
              onClick={async () => {
                setIsSubmitting(true);
                setSessionItem("signupRole", "leader");
                if (token) {
                  setSessionItem("pendingInviteToken", token);
                  setSessionItem("pendingInvite", JSON.stringify({ token }));
                }
                const nextPath = showSignIn ? "/today" : ONBOARDING_PATH;
                const { error } = await signInWithGoogle(nextPath, {
                  forceConsent: !showSignIn,
                  includeCalendarScope: false,
                });
                if (error) {
                  setIsSubmitting(false);
                  removeSessionItem("signupRole");
                  toast.error(error.message || "Google sign-in failed");
                }
              }}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-11"
              disabled={isSubmitting}
              onClick={async () => {
                setIsSubmitting(true);
                setSessionItem("signupRole", "leader");
                if (token) {
                  setSessionItem("pendingInviteToken", token);
                  setSessionItem("pendingInvite", JSON.stringify({ token }));
                }
                const nextPath = showSignIn ? "/today" : ONBOARDING_PATH;
                const { error } = await signInWithAzure(nextPath);
                if (error) {
                  setIsSubmitting(false);
                  removeSessionItem("signupRole");
                  toast.error(error.message || "Microsoft sign-in failed");
                }
              }}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              Continue with Microsoft
            </Button>

            {!showSignIn && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                <Shield className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Your coach will be able to see patterns from recent moments, not your private notes.
                </p>
              </div>
            )}
          </form>

          <div className="mt-6 space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              {showSignIn ? "New to Veera?" : "Already use Veera?"}{" "}
              <button
                type="button"
              onClick={() => {
                  setShowSignIn(!showSignIn);
                  setEmail("");
                  setPassword("");
                  setConfirmPassword("");
                  setFullName("");
                  setPasswordError("");
                }}
                className="text-primary hover:underline font-medium"
              >
                {showSignIn ? "Create a leader account" : "Sign in to connect"}
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
